import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

export type SelectOptionType = {text: string | React.ReactNode; value: string | number | undefined};

type SelectProps = {
  id?: string;
  value?: string | number | (string | number)[];
  onChange?: (value: string | number | (string | number)[] | undefined) => void;
  placeHolder?: string | React.ReactNode;
  options: SelectOptionType[];
  title?: string | React.ReactNode;
  label: string;
  showLabel?: boolean;
  styles?: SelectClasses;
};

type SelectClasses = {
  wrapper?: string;
  option?: string;
  icon?: string;
};

const Select: React.FC<SelectProps> = ({
  onChange = () => {
    return;
  },
  id = "SelectBox",
  value,
  options,
  placeHolder,
  title = "",
  label,
  showLabel = true,
  styles = {
    wrapper: "",
    option: "",
    icon: "",
  },
}) => {
  const {t} = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [focusMarker, setFocusMarker] = useState<boolean>(false);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const selectedOption = options.find((option) => option.value === value);

  const toggleListBox = () => {
    setIsOpen(!isOpen);
  };
  const closeListBox = () => {
    setIsOpen(false);
    setFocusedIndex(null);
    setFocusMarker(false);
  };

  const handleOptionSelect = (option: SelectOptionType) => {
    onChange(option.value);
    closeListBox();
  };

  // accessible keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setIsOpen(true);
      setFocusedIndex(0);

      return;
    }

    if (isOpen) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prevFocus) => {
            return prevFocus === null ? 0 : (prevFocus + 1) % options.length;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prevFocus) => {
            return prevFocus === null ? options.length - 1 : (prevFocus - 1 + options.length) % options.length;
          });
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (focusedIndex !== null) {
            handleOptionSelect(options[focusedIndex]);
          }
          break;
        case "Tab":
          closeListBox();
          break;
        case "Escape":
          e.preventDefault();
          closeListBox();
          break;
        default:
          break;
      }
    }
    setFocusMarker(true);
  };

  // close selectbox when user clicks outside selectbox area
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toggleButtonRef.current && !toggleButtonRef.current.contains(event.target as Node)) {
        closeListBox();
      }
    };
    document.addEventListener("click", handleClickOutside);

    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const isOneOrLessOption = options.length <= 1;

  // styles handling design and behaviour
  const containerClassName = [
    "dd__wrapper",
    styles.wrapper,
    isOpen ? "dd--open" : "",
    isOneOrLessOption ? "dd--unselectable" : "",
  ].join(" ");

  return (
    <>
      <label id={`${id}-label`} className={showLabel ? "dd__label" : "visually-hidden"}>
        {label}
      </label>
      <div id={id} className={containerClassName}>
        <button
          ref={toggleButtonRef}
          type="button"
          role="combobox"
          value="Select"
          aria-labelledby={`${id}-label`}
          aria-controls={`${id}-ListBox`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={toggleListBox}
          onKeyDown={(e) => handleKeyDown(e)}
        >
          {selectedOption ? (
            <span className="dd__title">
              {title} {selectedOption.text}
            </span>
          ) : (
            <span className="dd--placeholder">{placeHolder ?? t("form.pleaseChoose")}</span>
          )}
          {!isOneOrLessOption && (
            <span className={`icon icon-size--18 ${styles.icon ?? "icon-chevron-down"}`}>&#160;</span>
          )}
        </button>
        {isOpen && options.length > 0 && (
          <ul
            role="listbox"
            id={`${id}-ListBox`}
            aria-labelledby={`${id}-label`}
            className="dd__content list-style-none"
          >
            {options.map((option, index) => (
              <li
                key={`${id}-Option-${index}`}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                className={`dd__option ${styles.option ?? ""} ${selectedOption === option ? "dd__option--selected" : ""} ${focusMarker && focusedIndex === index ? "dd__option--focused" : ""}`}
                onClick={() => {
                  handleOptionSelect(option);
                }}
                onMouseEnter={() => setFocusedIndex(index)}
                onKeyDown={(e) => handleKeyDown(e)}
              >
                {option.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default Select;
